/**
 * Officer session. Calls `GET /auth/me` on the api (cookie-auth) to learn
 * whether the current browser is a logged-in officer, and which guilds they
 * manage. The planner view can gate on `authenticated` / `ready`.
 *
 * This is the **host-owned** owner of session state in Phase 2 — previously
 * the same file (under `composables/useSession.ts`) was a tiny shared
 * module. After Phase 2:
 *
 * - The `session` and `sessionLoading` refs + the `fetchSession` /
 *   `loginWithDiscord` / `logout` / `isMemberOf` / `isOfficerOf` /
 *   `isOwnerOf` / `displayUsername` helpers live here, exported as
 *   module-level singletons (same way as before — the migration is
 *   non-breaking because no consumer imports a *value* yet, only the port).
 *
 * - `provideSession(app)` wires the `SESSION_PORT` with a thin adapter
 *   over those module-level singletons. Components / stores that want to
 *   test against an isolated session can wrap their app in a different
 *   `provide(SESSION_PORT, …)`.
 *
 * - `useSession()` (re-exported from `@/shared/ports/session`) resolves
 *   the port. The legacy `composables/useSession.ts` shim re-exports the
 *   same values for the Phase 4 cleanup window.
 */
import { inject, ref, type App, type Ref } from 'vue'

import { apolloClient } from '@/shared/api/apolloClient'
import {
  SESSION_PORT,
  _setSessionPortFallback,
  type SessionPort,
  type SessionUser,
  type SessionGuild,
} from '@/shared/ports/session'

/** Re-export the session-domain types so app consumers can `import { … } from '@/app/session'`. */
export type { SessionUser, SessionGuild } from '@/shared/ports/session'

/** Both Vite and the production web container proxy same-origin `/auth`
 * requests. Split-origin deployments can still provide an explicit API URL. */
const API_BASE = import.meta.env.DEV
  ? ''
  : import.meta.env['VITE_API_BASE_URL'] ?? ''

/** The resolved session, or `null` if not logged in. */
export const session: Ref<SessionUser | null> = ref<SessionUser | null>(null)
/** `true` until the first `/auth/me` resolves. */
export const sessionLoading: Ref<boolean> = ref<boolean>(true)

/** Check the current session against the api. Call on app boot. */
export async function fetchSession(): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/auth/me`, {
      credentials: 'include',
      cache: 'no-store',
    })
    if (!res.ok) {
      session.value = null
      return
    }
    const body = (await res.json()) as {
      userId?: string | number
      user_id?: string | number
      discordUserId?: string | number
      discord_user_id?: string | number
      username?: string
      avatar?: string | null
      officerFor?: Array<string | number>
      officer_for?: Array<string | number>
      ownerFor?: Array<string | number>
      owner_for?: Array<string | number>
      memberFor?: Array<string | number>
      member_for?: Array<string | number>
      officerForNames?: Array<[string | number, string]> | Array<{ id: string | number; name: string }>
      officer_for_names?: Array<[string | number, string]> | Array<{ id: string | number; name: string }>
      ownerForNames?: Array<[string | number, string]> | Array<{ id: string | number; name: string }>
      owner_for_names?: Array<[string | number, string]> | Array<{ id: string | number; name: string }>
      memberForNames?: Array<[string | number, string]> | Array<{ id: string | number; name: string }>
      member_for_names?: Array<[string | number, string]> | Array<{ id: string | number; name: string }>
    } | null

    if (body && (body.discordUserId != null || body.discord_user_id != null)) {
      function mapIdsWithNames(
        ids: ReadonlyArray<string | number>,
        namesField?:
          | Array<[string | number, string]>
          | Array<{ id: string | number; name: string }>,
      ): ReadonlyArray<SessionGuild> {
        const names = new Map<string, string>()
        if (Array.isArray(namesField)) {
          for (const entry of namesField) {
            if (Array.isArray(entry)) {
              const [id, name] = entry
              if (typeof name === 'string') names.set(String(id), name)
            } else if (
              entry &&
              typeof entry.name === 'string'
            ) {
              names.set(String(entry.id), entry.name)
            }
          }
        }
        return ids.map((id) => {
          const k = String(id)
          return { id: k, name: names.get(k) ?? k }
        })
      }
      const userId = String(body.userId ?? body.user_id ?? '0')
      const discordUserId = String(body.discordUserId ?? body.discord_user_id ?? '0')
      const officerFor = mapIdsWithNames(
        body.officerFor ?? body.officer_for ?? [],
        body.officerForNames ?? body.officer_for_names,
      )
      const ownerFor = mapIdsWithNames(
        body.ownerFor ?? body.owner_for ?? [],
        body.ownerForNames ?? body.owner_for_names,
      )
      const memberFor = mapIdsWithNames(
        body.memberFor ?? body.member_for ?? [],
        body.memberForNames ?? body.member_for_names,
      )
      session.value = {
        userId,
        discordUserId,
        username: body.username ?? '',
        avatar: body.avatar ?? null,
        officerFor,
        ownerFor,
        memberFor,
      }
    } else {
      session.value = null
    }
  } catch {
    session.value = null
  } finally {
    sessionLoading.value = false
  }
}

/**
 * Redirect to Discord OAuth2. `guildId` is the signed-out browser's current
 * Discord-guild context, passed as a string so the full 64-bit snowflake
 * reaches the api intact. It can guide a target-role lookup after login, but
 * it never grants or restricts access; the server derives accessible guilds
 * from Discord and enforces authorization per resource.
 */
export function loginWithDiscord(guildId: string | number): void {
  const key = String(guildId)
  window.location.href = `${API_BASE}/auth/discord?guild_id=${key}`
}

/** Log out: clears the server session + cookie, then refreshes session state. */
export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-Requested-With': 'wwm-gvg',
    },
  })
  await fetchSession()
}

/** True if the session is at least a Discord member of the given guild
 * (i.e. allowed to view the board, even without officer/owner rights). */
export function isMemberOf(guildId: string | number): boolean {
  const key = String(guildId)
  return session.value?.memberFor.some((g) => g.id === key) ?? false
}

/** True if the session can **edit** the given guild (officer or owner). */
export function isOfficerOf(guildId: string | number): boolean {
  const key = String(guildId)
  const s = session.value
  if (!s) return false
  return (
    s.officerFor.some((g) => g.id === key) || s.ownerFor.some((g) => g.id === key)
  )
}

/** True if the session is the Discord owner of the given guild. */
export function isOwnerOf(guildId: string | number): boolean {
  const key = String(guildId)
  return session.value?.ownerFor.some((g) => g.id === key) ?? false
}

/** Strip the legacy `#1234` discriminator Discord adds when global_name is
 * unset. Used wherever the planner displays a username. */
export function displayUsername(username: string): string {
  const hash = username.indexOf('#')
  return hash >= 0 ? username.slice(0, hash) : username
}

/** Adapter that satisfies the `SessionPort` contract by delegating to the
 * module-level singletons above. The port interface pins `login()` as
 * `() => Promise<void>` so this adapter is shaped as the port — the
 * feature store (or any caller) can pick up the port without knowing
 * the legacy `guildId` argument exists.
 */
const sessionPortImpl: SessionPort = {
  user: session,
  loading: sessionLoading,
  isMemberOf,
  isOfficerOf,
  isOwnerOf,
  login: () => Promise.resolve(loginWithDiscord('')),
  logout: () => logout(),
}

/**
 * Wire the implementation into the Vue injection key. `main.ts` calls
 * this **after** `createApp(App)` and **before** `app.mount('#app')`.
 *
 * `fetchSession()` is **not** called from here — the boot path awaits
 * the first `/auth/me` before mounting the app so the first paint is
 * the authenticated state.
 */
/**
 * Wire the implementation into the Vue injection key AND register
 * the non-injection fallback so non-setup callers (`installGvgPlanner`,
 * `gvgPlannerNavLinks`, host test harnesses) reach the same instance
 * via `useSession()` without throwing `SessionPort not provided`.
 */
export function provideSession(app: App): void {
  app.provide(SESSION_PORT, sessionPortImpl)
  _setSessionPortFallback(sessionPortImpl)
}

/**
 * Host-side `useSession()` replacement: prefers the provided port, and
 * falls back to the module-level singletons in tests / edge contexts
 * where no app was wired. Identical contract to
 * `@/shared/ports/session`'s `useSession()` except for the fallback.
 */
export function useSessionOrFallback(): SessionPort {
  const fromPort = inject(SESSION_PORT, null)
  if (fromPort) return fromPort
  return sessionPortImpl
}

// Touch apolloClient so the import isn't tree-shaken before the provider wires
// it; the session flow is REST, but keeping the apollo import here documents
// that the two share the same api origin.
void apolloClient
